import { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Grid from '@mui/material/Grid2';
import { Card, Box, Typography, Button, TextField, Stack } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { TextEditedContent } from 'components';
import api from 'utils/api';
import { DOLPHIN_API } from 'shared/constants/apis';
import { AdminModal } from '../components/styles';
import { useSnackbar } from 'notistack';

const propTypes = {
  close: PropTypes.func.isRequired,
  fetchEmployments: PropTypes.func.isRequired,
};

const IWGDashboardEmploymentsNew = ({ close, fetchEmployments }) => {
  const [formState, setFormState] = useState({
    organizationCode: '',
    organizationId: -1,
    username: '',
    userId: -1,
    userType: '',
    isOwner: false,
  });
  const { enqueueSnackbar } = useSnackbar();
  const [incompleteFields, setIncompleteFields] = useState([]);
  const [incompleteWarningModalOpen, setIncompleteWarningModalOpen] =
    useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [options, setOptions] = useState({
    organizationOptions: [],
    userOptions: [],
  });

  useEffect(() => {
    const getOptions = async () => {
      const organizations = await api.get(DOLPHIN_API.ADMIN_ORGS, {});
      const organizationOptions = organizations.map((org) => {
        return {
          label: org.organizationCode + ' (' + org.name + ')',
          value: {
            organizationId: org.id,
            organizationCode: org.organizationCode,
          },
        };
      });
      const users = await api.get(DOLPHIN_API.ADMIN_USERS, {});
      const userOptions = users.map((user) => {
        return {
          label: user.username + ' (' + user.userType + ')',
          value: {
            userId: user.id,
            username: user.username,
          },
        };
      });
      setOptions({
        organizationOptions: organizationOptions,
        userOptions: userOptions,
      });
    };
    getOptions();
  }, []);

  const keyToCheck = ['organizationCode', 'username', 'userType'];

  const keyToLabel = {
    organizationCode: 'Organization Code',
    username: 'Username',
    userType: 'User Type',
  };

  const onValueChange = (key, value) => {
    setFormState((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const sendToServer = async () => {
    const data = { ...formState };
    await api.post(DOLPHIN_API.ADMIN_EMPLOYMENTS, {
      data: {
        createdData: data,
      },
    });
    await fetchEmployments();
    close();
    enqueueSnackbar('Employment created successfully', {
      variant: 'success',
    });
  };

  const checkIfFormCompleted = () => {
    const _incompleteFields = [];

    keyToCheck.forEach((key) => {
      const value = formState[key];
      if (value.length === 0) {
        _incompleteFields.push(keyToLabel[key]);
      }
    });

    if (_incompleteFields.length > 0) {
      setIncompleteFields(_incompleteFields);
      return false;
    } else {
      return true;
    }
  };

  const handleSubmitClick = async (event) => {
    event.preventDefault();
    if (!checkIfFormCompleted()) {
      setIncompleteWarningModalOpen(true);
    } else {
      setConfirmModalOpen(true);
    }
  };

  return (
    <Fragment>
      <Box py={3} px={3} mt={3} mx={3}>
        <Stack spacing={2}>
          <Autocomplete
            disablePortal
            id="organizationCode"
            onChange={(event, newValue) => {
              if (newValue && newValue.value) {
                onValueChange(
                  'organizationCode',
                  newValue.value.organizationCode
                );
                onValueChange('organizationId', newValue.value.organizationId);
              }
            }}
            options={options.organizationOptions}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  name="organizationCode"
                  fullWidth
                  variant="outlined"
                  label="Organization Code"
                  color="secondary"
                />
              );
            }}
          />

          <Autocomplete
            disablePortal
            id="username"
            onChange={(event, newValue) => {
              if (newValue && newValue.value) {
                onValueChange('username', newValue.value.username);
                onValueChange('userId', newValue.value.userId);
              }
            }}
            options={options.userOptions}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  name="username"
                  fullWidth
                  variant="outlined"
                  label="Username"
                  color="secondary"
                />
              );
            }}
          />

          <Autocomplete
            disablePortal
            id="userType"
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            onChange={(event, newValue) => {
              if (newValue && newValue.value)
                onValueChange('userType', newValue.value);
            }}
            options={[
              { label: 'Staff', value: 'staff' },
              { label: 'Doctor', value: 'doctor' },
              { label: 'Manager', value: 'manager' },
            ]}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  name="userType"
                  fullWidth
                  variant="outlined"
                  label="User Type"
                  color="secondary"
                />
              );
            }}
          />

          <Autocomplete
            disablePortal
            id="owner"
            onChange={(event, newValue) => {
              if (newValue) {
                onValueChange('isOwner', newValue.value);
              }
            }}
            options={[
              {
                label: 'Yes',
                value: true,
              },
              {
                label: 'No',
                value: false,
              },
            ]}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  name="owner"
                  fullWidth
                  variant="outlined"
                  label="Is Owner?"
                  color="secondary"
                />
              );
            }}
          />
          <Grid
            spacing={3}
            container
            alignItems="space=between"
            justifyContent="center">
            <Grid>
              <Button
                variant="contained"
                color="primary"
                style={{
                  color: 'white',
                }}
                onClick={handleSubmitClick}>
                Submit
              </Button>
            </Grid>
            <Grid>
              <Button variant="contained" color="secondary" onClick={close}>
                Cancel
              </Button>
            </Grid>
          </Grid>
        </Stack>

        {/* Modal for warning */}
        <AdminModal
          open={incompleteWarningModalOpen}
          onClose={() => setIncompleteWarningModalOpen(false)}>
          <Card sx={{ margin: 4, marginTop: 3, width: 800 }}>
            <Stack spacing={2} py={3} px={3} mt={3} mx={3}>
              <Typography mb={1}>Warning</Typography>
              <Typography>The following information is incomplete!</Typography>
              <br />
              {incompleteFields &&
                incompleteFields.map((field, index) => (
                  <p style={{ color: 'red' }} key={index}>
                    {field}
                  </p>
                ))}
              <br />
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setIncompleteWarningModalOpen(false)}>
                Close
              </Button>
            </Stack>
          </Card>
        </AdminModal>
        {/* Modal for confirm */}
        <AdminModal
          open={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}>
          <Card sx={{ margin: 4, marginTop: 3, width: 800 }}>
            <Stack spacing={2} py={3} px={3} mt={3} mx={3}>
              <Typography mb={1}>Confirm</Typography>
              <Typography>
                You are going to create a new employment as below:
              </Typography>
              <TextEditedContent
                content={JSON.stringify(formState, undefined, 4)}
              />
              <Grid
                spacing={3}
                container
                justifyContent="space-between"
                alignItems="center">
                <Grid>
                  <Button
                    variant="contained"
                    color="primary"
                    style={{
                      color: 'white',
                    }}
                    onClick={sendToServer}>
                    Confirm
                  </Button>
                </Grid>
                <Grid>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={() => {
                      setIncompleteWarningModalOpen(false);
                      setConfirmModalOpen(false);
                    }}>
                    Cancel
                  </Button>
                </Grid>
              </Grid>
            </Stack>
          </Card>
        </AdminModal>
      </Box>
    </Fragment>
  );
};

IWGDashboardEmploymentsNew.propTypes = propTypes;

export default IWGDashboardEmploymentsNew;
